/**
 * 추천인 시스템 API — 코드 발급/조회, 등록, 현황, 보상 청구.
 * 모든 요청은 Cloud Functions를 경유합니다(Supabase에는 직접 접근하지 않음).
 */

import { getAPIUrls } from './api';
import { bearerHeaders } from './authHeader';

const REFERRAL_STATUS_CACHE_TTL_MS = 60 * 1000;
const REFERRAL_STATUS_CACHE_PREFIX = 'referral_status_v1';

function getStatusCacheKey(userId, environment) {
  return `${REFERRAL_STATUS_CACHE_PREFIX}:${environment || 'production'}:${userId}`;
}

export function getCachedReferralStatus(userId, environment) {
  if (!userId || typeof window === 'undefined') return null;
  try {
    if (!window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(getStatusCacheKey(userId, environment));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.savedAt || Date.now() - cached.savedAt > REFERRAL_STATUS_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(getStatusCacheKey(userId, environment));
      return null;
    }
    return cached.data || null;
  } catch {
    return null;
  }
}

function cacheReferralStatus(userId, environment, data) {
  if (!userId || typeof window === 'undefined') return;
  try {
    if (!window.sessionStorage) return;
    window.sessionStorage.setItem(
      getStatusCacheKey(userId, environment),
      JSON.stringify({ savedAt: Date.now(), data })
    );
  } catch {
    // 저장 공간 제한/프라이버시 모드에서는 네트워크 응답만 사용합니다.
  }
}

export function invalidateReferralStatusCache(userId, environment) {
  if (!userId || typeof window === 'undefined') return;
  try {
    if (!window.sessionStorage) return;
    window.sessionStorage.removeItem(getStatusCacheKey(userId, environment));
  } catch {
    // 캐시 삭제 실패가 추천인 API 동작을 막아서는 안 됩니다.
  }
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Request failed');
    err.code = data.code || data.error;
    throw err;
  }
  return data;
}

/**
 * 내 추천 코드 조회/발급
 * @param {string} userId
 * @param {string} [environment]
 * @returns {Promise<{ code: string }>}
 */
export async function getMyReferralCode(userId, environment) {
  const urls = getAPIUrls();
  return postJson(urls.getMyReferralCode, { userId, environment });
}

/**
 * 추천인 코드 등록
 * @param {string} userId
 * @param {string} code
 * @param {string} [environment]
 * @returns {Promise<{ success: boolean }>}
 */
export async function registerReferral(userId, code, environment) {
  const urls = getAPIUrls();
  const result = await postJson(urls.registerReferral, { userId, code, environment });
  invalidateReferralStatusCache(userId, environment);
  return result;
}

/**
 * 내 추천 현황 (코드, pending/activated 개수, 청구한 등급, 등급 정의)
 * @param {string} userId
 * @param {string} [environment]
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<{ code: string, pendingCount: number, activatedCount: number, claimedTiers: number[], tiers: Array }>}
 */
export async function getReferralStatus(userId, environment, options = {}) {
  if (!options.forceRefresh) {
    const cached = getCachedReferralStatus(userId, environment);
    if (cached) return cached;
  }
  const urls = getAPIUrls();
  const result = await postJson(urls.getReferralStatus, { userId, environment });
  cacheReferralStatus(userId, environment, result);
  return result;
}

/**
 * 등급 보상 청구
 * @param {string} userId
 * @param {number} tier
 * @param {string} [environment]
 */
export async function claimReferralReward(userId, tier, environment) {
  const urls = getAPIUrls();
  const result = await postJson(urls.claimReferralReward, { userId, tier, environment });
  invalidateReferralStatusCache(userId, environment);
  return result;
}
